import { BreakpointObserver } from "@angular/cdk/layout";
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { NxButtonModule } from "@aposin/ng-aquila/button";
import { NxCopytextModule } from "@aposin/ng-aquila/copytext";
import { NxGridModule } from "@aposin/ng-aquila/grid";
import { NxHeaderModule } from "@aposin/ng-aquila/header";
import { NxIconModule } from "@aposin/ng-aquila/icon";
import { NxLinkModule } from "@aposin/ng-aquila/link";
import { NxMenuModule } from "@aposin/ng-aquila/menu";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Store } from "@ngxs/store";
import { AuthSessionFacade } from "@services/auth-session/auth-session.facade";
import { CrossAppNavigationService } from "@services/cross-app-navigation/cross-app-navigation.service";
import { NotificationFacade } from "@services/notification/notification.facade";
import { formatLocalDateTime } from "@shared/functions/date-utils";
import { UserResponse } from "@shared/interfaces/user.model";
import { LOGOUT } from "@store/user/user.action";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

import { NotificationSidebarComponent } from "../notification-sidebar/notification-sidebar.component";

@Component({
  selector: "app-header",
  standalone: true,
  imports: [
    CommonModule,
    NxButtonModule,
    NxCopytextModule,
    NxGridModule,
    NxHeaderModule,
    NxIconModule,
    NxLinkModule,
    NxMenuModule,
    NotificationSidebarComponent,
    TranslateModule,
  ],
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() public user: Partial<UserResponse> | null = null;

  @Output() public isShowMenu: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  protected menuPolicyMgmt: any[] = [];
  protected menuList: any[] = [];

  private userInfo: Partial<UserResponse> | null = null;
  protected unreadNotification$ = this.notificationFacade.unreadCount$;

  protected showmenu = true;
  protected screenWidth: number;
  protected isMobileScreen: boolean;
  protected showParentMenu: boolean;

  protected isNotificationClicked = false;

  protected get userName$() {
    const preferName =
      this.user?.preferName ??
      this.userInfo?.preferName ??
      this.userInfo?.name ??
      "User";
    return this.translate
      .get("HEADER.WELCOME")
      .pipe(map((text: string) => `${text}, ${preferName}`));
  }

  protected get lastLogin$() {
    const lastLoginDate =
      this.user?.lastLoginDate ?? this.userInfo?.lastLoginDate ?? null;
    const formattedDate = formatLocalDateTime(lastLoginDate);
    return this.translate
      .get("HEADER.LAST_LOGIN_TIME")
      .pipe(map((text: string) => `${text}: ${formattedDate}`));
  }

  protected get isAdmin$() {
    if (this.userInfo?.menuAccess) {
      if (this.userInfo.menuAccess.includes("SAT_ADMIN_ALERT")) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  constructor(
    private readonly translate: TranslateService,
    private readonly store: Store,
    private readonly authSessionFacade: AuthSessionFacade,
    private readonly notificationFacade: NotificationFacade,
    private readonly elementRef: ElementRef,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly crossAppNavigationService: CrossAppNavigationService,
    private readonly router: Router,
  ) {
    this.screenWidth = window.innerWidth;
    // this.showmenu = this.screenWidth >= 993;
  }

  ngOnInit() {
    this.showParentMenu = false;

    this.userInfo = this.authSessionFacade.userSnapshot;

    this.authSessionFacade.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => (this.userInfo = user));

    this.authSessionFacade.policyMenu$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((menu: any) => {
        const subMenu = menu?.subMenu ?? [];
        this.menuPolicyMgmt = JSON.parse(JSON.stringify(subMenu));
      });

    this.authSessionFacade.menuList$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((menuList: any[]) => {
        const clonedMenuList = JSON.parse(JSON.stringify(menuList ?? []));
        this.menuList = this.sortTopLevelMenus(clonedMenuList);
      });

    this.breakpointObserver
      .observe(["(max-width: 993px)"])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result.matches) {
          this.isMobileScreen = true;
          this.showmenu = false;
        } else {
          this.isMobileScreen = false;
          this.showmenu = true;
        }
      });
  }

  // @HostListener("window:resize", ["$event"])
  // onResize(event: Event) {
  //   this.screenWidth = window.innerWidth;
  //   this.showmenu = this.screenWidth >= 993;
  // }

  private checkScreen() {
    const screenWidth = window.innerWidth;
    if (screenWidth < 993) {
      this.showmenu = false;
    }
  }

  protected toggleMenu() {
    this.showmenu = !this.showmenu;
  }

  protected onHoverToggleParentMenu(isOpen: boolean) {
    this.showParentMenu = isOpen;
    this.isNotificationClicked = false;
  }

  protected onHoverToggleNonPolicy(isOpen: boolean) {
    this.showParentMenu = isOpen;
  }

  protected toggleParentMenu() {
    this.showParentMenu = !this.showParentMenu;
    this.isNotificationClicked = false;
  }

  protected onClick(item: any) {
    item.hidden = !item.hidden;
  }

  protected nav(url: any, item: any) {
    item.expanded = !item.expanded;
    this.checkScreen();
    this.handleHeaderMenuNavigation(item, url);
  }

  protected open(submenu: any, parentMenu: any, menu: any) {
    this.checkScreen();
    submenu.expanded = !submenu.expanded;
    parentMenu.expanded = !parentMenu.expanded;
    menu.expanded = !menu.expanded;
    this.handleHeaderMenuNavigation(submenu, submenu?.link);
  }

  protected openURL(url, name, spec) {
    window.open(url, name, spec);
  }

  protected switchURL(url: any) {
    const localRoute = this.getLocalRouteForSwitchKey(url);
    if (localRoute) {
      void this.router.navigateByUrl(localRoute);
      return;
    }

    // Todo: remove these 4 lines once the alpha paths are fully switched over and we no longer need to check for them separately. The getDirectAlphaSatPathForSwitchKey function can then be removed as well, and all switchURL calls can just use getLocalRouteForSwitchKey.
    const alphaSatPath = this.getDirectAlphaSatPathForSwitchKey(url);
    if (alphaSatPath) {
      this.crossAppNavigationService.redirectToAlphaSatPath(alphaSatPath);
      return;
    }

    const queryParams = this.authSessionFacade.queryParamsSnapshot;
    this.crossAppNavigationService
      .resolveSatSwitchSite(url, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resolvedUrl) => {
        let switchURL = resolvedUrl;
        if (url == "SAT_MT_ENDT_SYS_URL" && queryParams["opportunityId"]) {
          if (!switchURL.includes("?")) {
            switchURL += queryParams["opportunityId"]
              ? `?opportunityId=${queryParams["opportunityId"]}`
              : "";
          } else {
            switchURL += queryParams["opportunityId"]
              ? `&opportunityId=${queryParams["opportunityId"]}`
              : "";
          }
        }
        this.crossAppNavigationService.redirect(switchURL);
      });
  }

  protected switchToAlpha() {
    if (this.userInfo?.userId) {
      this.crossAppNavigationService.redirect(environment.alpha_url);
    }
  }

  protected signOut() {
    this.store
      .dispatch(new LOGOUT())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private getSATRedirect(id: any) {
    return {
      MNDASHBRD: "SAT_SYS_URL",
      MNELIBRARY: "SAT_ELIBRARY_URL",
      MNSALESMNGT: "SAT_SALES_MANAGEMENT_URL",
      MNCUSTMNGT: "SAT_CUSTOMER_MANAGEMENT_URL",
    }[id];
  }

  private handleHeaderMenuNavigation(item: any, rawLink: any): void {
    const localRoute = this.getLocalRouteForMenu(rawLink);
    if (localRoute) {
      void this.router.navigateByUrl(localRoute);
      return;
    }

    const moduleKey = this.getModuleKeyForMenu(item, rawLink);

    if (moduleKey) {
      this.switchURL(moduleKey);
    }
  }

  private getLocalRouteForMenu(rawLink: any): string | null {
    if (typeof rawLink !== "string") {
      return null;
    }

    const normalizedLink = rawLink.trim().toLowerCase();

    return (
      {
        summary: "/summary",
        "/summary": "/summary",
        login: "/login",
        "/login": "/login",
      }[normalizedLink] ?? null
    );
  }

  private getModuleKeyForMenu(item: any, rawLink: any): string | null {
    const absoluteModuleKey = this.getModuleKeyFromAbsoluteLink(rawLink);
    if (absoluteModuleKey) {
      return absoluteModuleKey;
    }

    if (item?.type === "M" && item?.moduleOwner === "SAT") {
      return this.getSATRedirect(item.menuId) ?? this.toModuleKey(rawLink);
    }

    return this.toModuleKey(rawLink);
  }

  private getModuleKeyFromAbsoluteLink(rawLink: any): string | null {
    if (typeof rawLink !== "string" || !/^https?:\/\//i.test(rawLink)) {
      return null;
    }

    try {
      const lastSegment =
        new URL(rawLink).pathname
          .split("/")
          .map((segment) => segment.trim())
          .filter(Boolean)
          .pop()
          ?.toUpperCase() ?? "";

      return lastSegment.endsWith("_SYS_URL") ? lastSegment : null;
    } catch {
      return null;
    }
  }

  private toModuleKey(rawLink: any): string | null {
    if (typeof rawLink !== "string") {
      return null;
    }

    const normalizedLink = rawLink.trim().replace(/^\//, "");
    if (!normalizedLink) {
      return null;
    }

    const upperLink = normalizedLink.toUpperCase();
    return upperLink.endsWith("_SYS_URL") ? upperLink : `${upperLink}_SYS_URL`;
  }

  private getLocalRouteForSwitchKey(url: any): string | null {
    const normalizedKey =
      typeof url === "string" ? url.trim().toUpperCase() : "";

    return (
      {
        LOGIN_SYS_URL: "/login",
        SUMMARY_SYS_URL: "/summary",
      }[normalizedKey] ?? null
    );
  }

  // Todo: remove this function once the alpha paths are fully switched over and we no longer need to check for them separately. All switchURL calls can then just use getLocalRouteForSwitchKey.
  private getDirectAlphaSatPathForSwitchKey(url: any): string | null {
    const normalizedKey =
      typeof url === "string" ? url.trim().toUpperCase() : "";

    return (
      {
        SAT_SALES_MANAGEMENT_URL: "/home?token=TOKEN&action=SALES-MANAGEMENT",
        SAT_CUSTOMER_MANAGEMENT_URL:
          "/home?token=TOKEN&action=CUSTOMER-MANAGEMENT",
      }[normalizedKey] ?? null
    );
  }

  private sortTopLevelMenus(menuList: any[]): any[] {
    const preferredOrder = new Map<string, number>([
      ["MNDASHBRD", 1],
      ["SA_MNPOLICIES", 2],
      ["MNSALESMNGT", 3],
      ["MNCUSTMNGT", 4],
      ["MNELIBRARY", 5],
    ]);

    return (menuList ?? [])
      .map((item: any, index: number) => ({ item, index }))
      .sort((a, b) => {
        const aRank = preferredOrder.get(a.item?.menuId ?? "") ?? 999;
        const bRank = preferredOrder.get(b.item?.menuId ?? "") ?? 999;

        if (aRank !== bRank) {
          return aRank - bRank;
        }

        return a.index - b.index;
      })
      .map(({ item }) => item);
  }

  protected isUnifiedMenu(menuId: any) {
    return [
      "SA_POLSUMMARY",
      "SA_POLREPORTS",
      "SA_POLENQUIRY",
      "SA_POLCI",
    ].includes(menuId);
  }

  protected isMotorMenu(menuId: any) {
    return ["SAT_MT_MOTOR"].includes(menuId);
  }

  protected isNonMotorMenu(menuId: any) {
    return ["SA_NONMOTOR"].includes(menuId);
  }

  protected toggleNotification(event: any) {
    event.stopPropagation();
    if (this.isNotificationClicked) {
      this.isNotificationClicked = false;
      this.elementRef.nativeElement.ownerDocument.body.style.position = null;
      this.elementRef.nativeElement.ownerDocument.body.style.overflow = null;
    } else {
      this.isNotificationClicked = true;
      // Lock background scrolling on small screens when notifications open
      if (window.innerWidth < 993) {
        this.elementRef.nativeElement.ownerDocument.body.style.overflow =
          "hidden";
      }
      // update caret position once the panel is rendered
      setTimeout(() => this.updateNotificationCaretPosition(), 0);
    }
    this.showParentMenu = false;
  }

  protected toggledNotification($event: any) {
    this.isNotificationClicked = !!$event;
    if (!this.isNotificationClicked) {
      this.elementRef.nativeElement.ownerDocument.body.style.position = null;
      this.elementRef.nativeElement.ownerDocument.body.style.overflow = null;
    }
  }

  @HostListener("window:resize", ["$event"])
  protected onWindowResize(_event: Event) {
    if (this.isNotificationClicked) {
      this.updateNotificationCaretPosition();
    }
  }

  /**
   * Compute and set the horizontal position of the notification caret (triangle) so it
   * visually centers under the notification trigger icon.
   *
   * How it works:
   *  - Finds the trigger element (.notif-icon) and the popup panel (.header-notification__float-block).
   *  - Uses getBoundingClientRect() to get absolute positions for both elements.
   *  - Computes the caret's left offset relative to the popup: center of trigger minus half the caret width.
   *  - Writes the computed value to the CSS custom property --notif-caret-left on the popup (in pixels).
   *
   * Why a CSS variable:
   *  - Allows the caret pseudo-element in SCSS to read the value and position itself without DOM style hacks.
   *  - Keeps positioning responsive when the popup is re-rendered or resized.
   **/
  private updateNotificationCaretPosition(): void {
    try {
      const host: HTMLElement = this.elementRef.nativeElement;
      const trigger: HTMLElement | null = host.querySelector(
        ".notification-popover .notif-icon",
      );
      const popup: HTMLElement | null = host.querySelector(
        ".notification-popover .notification-panel .header-notification__float-block",
      );

      if (!trigger || !popup) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const popupRect = popup.getBoundingClientRect();

      // caret is 12px wide, center it under the trigger
      const caretHalf = 6;
      const caretLeft = Math.round(
        triggerRect.left + triggerRect.width / 2 - popupRect.left - caretHalf,
      );

      popup.style.setProperty("--notif-caret-left", `${caretLeft}px`);
    } catch (e) {
      // ignore errors; positioning is best-effort
    }
  }

  protected getNotificationIcon(
    unreadCount: number | null | undefined,
  ): string {
    const hasUnread = (unreadCount ?? 0) > 0;

    if (this.isNotificationClicked) {
      return hasUnread
        ? "assets/img/Icon - Notification - New - Active.svg"
        : "assets/img/Icon - Notification - Read - Active.svg";
    }

    return hasUnread
      ? "assets/img/Icon - Notification - New - Inactive.svg"
      : "assets/img/Icon - Notification - Read - Inactive.svg";
  }

  private redirectToDashboard() {
    this.crossAppNavigationService
      .resolveSatSwitchSite("SAT_SYS_URL")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  protected redirectToSummary() {
    this.switchURL("SUMMARY_SYS_URL");
  }
}
